import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ChevronUpCircleIcon,
  ChevronDownCircleIcon,
} from "lucide-react";

const formatPrice = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "decimal",
    maximumFractionDigits: 0,
  }).format(value);

const getPriceAndAreaDetails = (flatTypeArray) => {
  let smallest = null;
  let largest = null;

  flatTypeArray.forEach((ft) => {
    if (Array.isArray(ft.builtUpAreas)) {
      ft.builtUpAreas.forEach((area) => {
        const price = parseFloat(area.expectedPrice || 0);
        if (!isNaN(price)) {
          if (!smallest || price < smallest.expectedPrice) smallest = area;
          if (!largest || price > largest.expectedPrice) largest = area;
        }
      });
    }
  });

  return { smallest, largest };
};

const HomeFeatureCategory = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([]);
  const navigate = useNavigate();

  const parseArray = (input) => {
    if (Array.isArray(input)) return input;
    if (typeof input === "string") {
      try {
        return JSON.parse(input);
      } catch {
        return [];
      }
    }
    return [];
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(
        `https://100real.xyz/categoryfeature/getcategory/HBTR0007`
      );
      let res = await response.json();

      res = res.filter((category) => category.status === "Active");

      const flatSlides = [];

      res.forEach((category) => {
        let buildings = [];
        try {
          buildings = JSON.parse(category.buildings || "[]").map((building) => ({
            ...building,
            amenities: parseArray(building.amenities),
            flatType: parseArray(building.flatType),
            formImages: parseArray(building.formImages),
            locationAdvantages: parseArray(
              building.locationAdvantage || building.locationAdvantages
            ),
            security: parseArray(building.security),
            water_supply: parseArray(building.water_supply),
            building_name: building.building_name || "Unnamed Building",
            city: building.city || "Unknown city",
            categoryMeta: {
              propertyId: category.propertyId,
              propertyType: category.propertyType,
            },
          }));
        } catch (err) {
          console.error("Error parsing buildings:", err);
        }

        flatSlides.push(...buildings);
      });

      setSlides(flatSlides);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!slides.length) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [slides.length]);

  const handlePageChange = (propertyId, propertyType) => {
    if (propertyType === "Residential") {
      navigate(`/residential-property/${propertyId}`);
    } else if (propertyType === "Commercial") {
      navigate(`/commercial-property/${propertyId}`);
    } else if (propertyType === "Rent Residential") {
      navigate(`/rent-property/${propertyId}`);
    }
  };

  const building = slides[currentSlide];

  return (
    <div className="home-feature-category-first-brick">
      <div className="home-feature-category-flex-fb">
        <AnimatePresence mode="wait">
          {building && (
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.6 }}
              drag={window.innerWidth < 768 ? "x" : false}
              dragConstraints={{ left: 0, right: 0 }}
              onDragEnd={(e, info) => {
                if (info.offset.x < -100) {
                  setCurrentSlide((prev) => (prev + 1) % slides.length);
                } else if (info.offset.x > 100) {
                  setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
                }
              }}
              className="home-feature-category-card"
              onClick={() => handlePageChange(building.property_id, building.typeofproperty)}
              style={{ cursor: "pointer" }}
            >
              <div className="home-feature-property-leftbox" />
              <div className="home-feature-property-img-border" />
              <div className="home-feature-property-img">
                <motion.img
                  src={
                    building.formImages?.[0]
                      ? `https://100real.xyz/Images/Store/${building.formImages[0]}`
                      : "https://via.placeholder.com/300x200?text=No+Image"
                  }
                  alt={building.building_name || "Building"}
                  style={{ cursor: "pointer", width: "100%" }}
                  initial={{ opacity: 0.3, filter: "blur(4px)" }}
                  animate={{ opacity: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0.3, filter: "blur(4px)" }}
                  transition={{ duration: 0.5 }}
                />
              </div>

              <div className="home-feature-property-content">
                <h1 className="home-feature-property-building-name">
                  {building.building_name}
                </h1>
                <p className="home-feature-property-area-name">{building.city}</p>

                {(() => {
                  const { smallest, largest } = getPriceAndAreaDetails(
                    building.flatType || []
                  );
                  return smallest && largest ? (
                    <p className="home-feature-property-price">
                      {smallest?.expectedPrice > 0 && largest?.expectedPrice > 0 ? (
                        smallest.expectedPrice === largest.expectedPrice ? (
                          `₹ ${formatPrice(smallest.expectedPrice)}`
                        ) : (
                          `₹ ${formatPrice(
                            smallest.expectedPrice
                          )} - ₹ ${formatPrice(largest.expectedPrice)}`
                        )
                      ) : smallest?.expectedPrice > 0 ? (
                        `₹ ${formatPrice(smallest.expectedPrice)}`
                      ) : (
                        `₹ ${formatPrice(largest.expectedPrice)}`
                      )}
                    </p>
                  ) : (
                    <p className="home-feature-property-price">Price on request</p>
                  );
                })()}
              </div>

              {/* <div className="home-feature-property-logo">
                <h2>Sarfare Properties</h2>
              </div>

              <div className="home-feature-property-contact">
                <h3>+91-98765 43210</h3>
              </div> */}

              <div className="home-feature-property-rightbox" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Desktop Pagination */}
      <div className="vertical-pagination">
        <button
          className="vertical-chevron-icon"
          onClick={() =>
            setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)
          }
        >
          <ChevronUpCircleIcon size={30} />
        </button>
        <span className="vertical-line"></span>
        <span className="feature-current-slide-number">{currentSlide + 1}</span>
        <span className="vertical-line"></span>
        <button
          className="vertical-chevron-icon"
          onClick={() =>
            setCurrentSlide((prev) => (prev + 1) % slides.length)
          }
        >
          <ChevronDownCircleIcon size={30} />
        </button>
      </div>
    </div>
  );
};

export default HomeFeatureCategory;
